import { useMutation } from "@apollo/client";

import useLabel from "@apps/use-label/use-label";
import AiKnowledgeEditor from "@components/ai-knowledge-editor/ai-knowledge-editor";
import Heading from "@components/heading/heading";

import {
  CreateOrUpdateOrganizationSettingsMutation,
  CreateOrUpdateOrganizationSettingsMutationVariables,
  OrgSettingsOrganizationFragmentFragment,
} from "../../../types/graphql-schema";
import updateOrganizationMutation from "../graphql/update-organization-mutation";
import WorkspaceSkills from "./workspace-skills";

type Props = {
  organization: OrgSettingsOrganizationFragmentFragment;
};

const Ai = ({ organization }: Props) => {
  const label = useLabel();
  const [updateOrganization] = useMutation<
    CreateOrUpdateOrganizationSettingsMutation,
    CreateOrUpdateOrganizationSettingsMutationVariables
  >(updateOrganizationMutation);

  const handleSaveAiKnowledge = async (jsonValue: string) => {
    if (organization.aiGuidelines === jsonValue) return;
    await updateOrganization({
      variables: {
        organizationId: organization.id,
        aiGuidelines: jsonValue,
      },
      optimisticResponse: {
        createOrUpdateOrganization: {
          organization: {
            ...organization,
            aiGuidelines: jsonValue,
          },
          __typename: "CreateOrUpdateOrganizationMutation",
        },
        __typename: "Mutation",
      },
    });
  };

  return (
    <div aria-label="AI settings page">
      <Heading small title="AI" />
      <p className="text-sm text-gray-600 mt-2 mb-8">
        Manage how AskAI works for your {label("organization")}: shared skills
        any member can invoke, and {label("organization")}-wide knowledge that
        personalizes responses.
      </p>

      <WorkspaceSkills organizationId={organization.id} />

      {waffle.flag_is_active("ask-ai-guidelines") && (
        <div className="pt-8 border-t">
          <h3 className="text-base font-medium text-gray-800 mb-1">
            AI knowledge
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {label("organization", { capitalize: true })}-wide context like
            acronyms, internal processes, and response preferences so AskAI can
            tailor answers.
          </p>
          <AiKnowledgeEditor
            value={organization.aiGuidelines || null}
            onSave={handleSaveAiKnowledge}
            label="AI Knowledge"
            description={`Add ${label(
              "organization",
            )}-specific context like company acronyms, internal processes, and response preferences so AskAI can tailor answers.`}
            placeholder="e.g. Prioritize goal data, explain company acronyms, and reference internal processes."
          />
        </div>
      )}
    </div>
  );
};

export default Ai;
